import React, { useState, useCallback, useMemo } from 'react';
import InputField from 'components/form/input_field/InputField';
import Button from 'components/general/button/Button';
import FormInput from 'components/general/form_input/form_input';
import CityInput from 'components/registrationPage/CityInput';
import Select from 'components/form/select/select';
import Textarea from 'components/form/textarea/textarea';

interface WorkPermitData {
  kind?: string;
  number?: string;
  issuedCity?: string;
  issuedCountry?: string;
  issuedGooglePlaceId?: string;
  issuedCountryName?: string;
  expiresAt?: string;
  notes?: string;
}

interface WorkPermitKind {
  label: string;
  value: string;
}

interface ShareWorkPermitStepProps {
  jobId: string;
  applicationWorkPermit?: WorkPermitData;
  workPermitKinds?: WorkPermitKind[];
  onNext: (workPermitData: WorkPermitData) => void;
  onPrevious: () => void;
}

const FIELD_MAPPING = {
  expiresAt: 'expires_at',
  location: 'city',
  issuedCity: 'city',
  issuedCountry: 'country_code',
  issuedGooglePlaceId: 'google_place_id',
} as const;

const ShareWorkPermitStep: React.FC<ShareWorkPermitStepProps> = ({
  jobId,
  applicationWorkPermit = {},
  workPermitKinds = [],
  onNext,
  onPrevious,
}) => {

  const styles = require('./Step.module.scss');
  
  const getAuthenticityToken = useCallback(() => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }, []);
  
  const getKindOption = useCallback((kindValue: string) => {
    if (!kindValue || !workPermitKinds.length) return null;
    return workPermitKinds.find(option => option.value === kindValue);
  }, [workPermitKinds]);

  const [workPermit, setWorkPermit] = useState<WorkPermitData>({
    kind: applicationWorkPermit.kind,
    number: applicationWorkPermit.number,
    issuedCity: applicationWorkPermit.issuedCity,
    issuedCountry: applicationWorkPermit.issuedCountry,
    issuedGooglePlaceId: applicationWorkPermit.issuedGooglePlaceId,
    issuedCountryName: applicationWorkPermit.issuedCountryName,
    expiresAt: applicationWorkPermit.expiresAt,
    notes: applicationWorkPermit.notes,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const disableNext = useMemo(() => {
    return (
      isSubmitting ||
      Object.values(errors).some(error => error) ||
      !workPermit.kind?.trim() ||
      !workPermit.number?.trim() ||
      !workPermit.issuedCity?.trim() ||
      !workPermit.expiresAt?.trim()
    );
  }, [isSubmitting, errors, workPermit]);

  const clearErrors = useCallback((fieldNames: string[]) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      fieldNames.forEach(fieldName => {
        delete newErrors[fieldName];
        const backendField = FIELD_MAPPING[fieldName as keyof typeof FIELD_MAPPING];
        if (backendField) {
          delete newErrors[backendField];
        }
      });
      return newErrors;
    });
  }, []);

  const extractLocationData = useCallback((event: any) => {
    const city = event.formatted_address.split(', ')[0];
    const countryComponent = event.address_components.find(
      (addressComponent: { types: string | string[] }) => addressComponent.types.includes('country')
    );
    
    return {
      city,
      countryShort: countryComponent?.short_name || '',
      countryLong: countryComponent?.long_name || '',
      googlePlaceId: event.place_id,
    };
  }, []);

  const handleWorkPermitChange = useCallback((event: any, input: string) => {
    clearErrors([input]);
    
    if (input === 'location') {
      clearErrors(['location', 'city']);
      const locationData = extractLocationData(event);
      
      setWorkPermit(prev => ({
        ...prev,
        issuedCity: locationData.city,
        issuedCountry: locationData.countryShort,
        issuedCountryName: locationData.countryLong,
        issuedGooglePlaceId: locationData.googlePlaceId,
      }));
    } else {
      setWorkPermit(prev => ({ ...prev, [input]: event }));
    }
  }, [clearErrors, extractLocationData]);

  const formatBackendErrors = useCallback((backendErrors: Record<string, string[] | string>) => {
    const formattedErrors: Record<string, string> = {};
    
    Object.entries(backendErrors).forEach(([key, value]) => {
      const fieldName = Object.entries(FIELD_MAPPING).find(
        ([, backendField]) => backendField === key
      )?.[0] || key;
      
      formattedErrors[fieldName] = Array.isArray(value) ? value[0] : value;
    });
    
    return formattedErrors;
  }, []);

  const handleNext = useCallback(async () => {
    if (disableNext) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(
        window.Routes.work_permit_job_ad_quick_applications_url(jobId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getAuthenticityToken(),
          },
          body: JSON.stringify({
            work_permit: {
              kind: workPermit.kind || '',
              number: workPermit.number,
              city: workPermit.issuedCity,
              country_code: workPermit.issuedCountry,
              google_place_id: workPermit.issuedGooglePlaceId,
              expires_at: workPermit.expiresAt,
              notes_has_permit: workPermit.notes,
              in_process: false,
            },
          }),
        }
      );

      const data = await response.json();
      
      setIsSubmitting(false);

      if (response.ok && data.success) {
        onNext(workPermit);
      } else if (data.errors) {
        setErrors(formatBackendErrors(data.errors));
      }
    } catch (error) {
      // Error handling
    }
  }, [disableNext, jobId, getAuthenticityToken, workPermit, onNext, formatBackendErrors]);


  return (
    <>
      <div className={styles.form}>
        <FormInput name="kind" label="Document Type" required labelClass={styles.inputsText} error={errors.kind}>
          <Select
            name='work_permit[kind]'
            placeholder="Select one"
            options={workPermitKinds}
            isMulti={false}
            color='silverSand'
            value={getKindOption(workPermit.kind)}
            onChange={(event) => handleWorkPermitChange(event.value, 'kind')}
          />
        </FormInput>
      
        <InputField
          id="number"
          name="work_permit[number]"
          label="Document Number"
          placeholder="XYZ12345"
          value={workPermit.number}
          onChange={(e) => handleWorkPermitChange(e.target.value, 'number')}
          required
          error={errors.number}
        />

        <CityInput
          error={errors.location}
          class={styles.actionButton}
          handleFormChange={handleWorkPermitChange}
          city={workPermit.issuedCity}
          country={workPermit.issuedCountry}
          countryName={workPermit.issuedCountryName}
          label={'Issued in'}
          labelClass={styles.inputsText}
        />
      
        <InputField
          id="expires_at"
          name="work_permit[expires_at]"
          label="Expires at"
          type="date"
          value={workPermit.expiresAt}
          onChange={(e) => handleWorkPermitChange(e.target.value, 'expiresAt')}
          required
          error={errors.expiresAt}
        />

        <FormInput
          label="Additional information (optional)"
          labelClass={styles.inputsText}
        >
          <Textarea
            placeholder="Any other information you find relevant about your current situation"
            className={styles.questionTextarea}
            defaultValue={workPermit.notes}
            handleChange={(e) => handleWorkPermitChange(e.target.value, 'notes')}
            rows={4}
            maxLength={500}
            showCharacterCount
          />
        </FormInput>
      </div>

      <div className={styles.footer}>
        <Button
          onClick={onPrevious}
          type="Secondary"
          size="M"
          iconLeftName="chevronLeft"
          className={styles.actionButton}
        >
          Previous
        </Button>

        <Button
          onClick={handleNext}
          disabled={disableNext}
          size="M"
          iconRightName="chevronRight"
          className={styles.actionButton}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default ShareWorkPermitStep;
